import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal } from '@arco-design/web-react';

export type RegionCoords = { x: number; y: number; w: number; h: number };

type Props = {
  visible: boolean;
  imageUrl: string | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (region: RegionCoords, instruction: string) => void;
};

export default function RegionEditor({ visible, imageUrl, submitting, onCancel, onSubmit }: Props) {
  const [region, setRegion] = useState<RegionCoords | null>(null);
  const [instruction, setInstruction] = useState('');
  const [dragging, setDragging] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [panning, setPanning] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [fit, setFit] = useState<{ w: number; h: number } | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const panRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible) {
      setRegion(null);
      setInstruction('');
      setDragging(false);
      setSelectMode(false);
      setPanning(false);
      setZoom(1);
      setFit(null);
      startRef.current = null;
      panRef.current = null;
    }
  }, [visible, imageUrl]);

  const clampZoom = (value: number) => Math.min(4, Math.max(1, Math.round(value * 100) / 100));

  const handleImageLoad = () => {
    const image = imageRef.current;
    const viewport = viewportRef.current;
    if (!image || !viewport) return;
    const maxWidth = viewport.clientWidth;
    const maxHeight = window.innerHeight * 0.65;
    const scale = Math.min(maxWidth / image.naturalWidth, maxHeight / image.naturalHeight, 1);
    setFit({
      w: Math.max(1, Math.round(image.naturalWidth * scale)),
      h: Math.max(1, Math.round(image.naturalHeight * scale))
    });
  };

  const zoomed = zoom > 1 && fit;

  const toPoint = (event: React.MouseEvent) => {
    const image = imageRef.current;
    if (!image) return null;
    const rect = image.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
    };
  };

  const handleDown = (event: React.MouseEvent) => {
    const point = toPoint(event);
    if (!point) return;
    event.preventDefault();
    startRef.current = point;
    setRegion({ x: point.x, y: point.y, w: 0, h: 0 });
    setDragging(true);
  };

  const handleMove = (event: React.MouseEvent) => {
    if (!dragging || !startRef.current) return;
    const point = toPoint(event);
    if (!point) return;
    const start = startRef.current;
    setRegion({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      w: Math.abs(point.x - start.x),
      h: Math.abs(point.y - start.y)
    });
  };

  const handleUp = () => {
    if (!dragging) return;
    setDragging(false);
    setRegion(prev => (prev && (prev.w < 0.01 || prev.h < 0.01) ? null : prev));
  };

  const handleCanvasDown = (event: React.MouseEvent) => {
    if (selectMode) {
      handleDown(event);
      return;
    }
    const viewport = viewportRef.current;
    if (!viewport) return;
    event.preventDefault();
    panRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: viewport.scrollLeft,
      top: viewport.scrollTop
    };
    setPanning(true);
  };

  const handleCanvasMove = (event: React.MouseEvent) => {
    if (panRef.current) {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const pan = panRef.current;
      viewport.scrollLeft = pan.left - (event.clientX - pan.x);
      viewport.scrollTop = pan.top - (event.clientY - pan.y);
      return;
    }
    if (selectMode) {
      handleMove(event);
    }
  };

  const handleCanvasUp = () => {
    panRef.current = null;
    setPanning(false);
    if (selectMode) {
      handleUp();
    }
  };

  const handleCanvasDoubleClick = () => {
    startRef.current = null;
    setDragging(false);
    setSelectMode(prev => !prev);
  };

  const canSubmit = Boolean(region && instruction.trim()) && !submitting;

  const handleInstructionKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !(event.nativeEvent as any).isComposing) {
      if (canSubmit && region) {
        event.preventDefault();
        onSubmit(region, instruction.trim());
      }
    }
  };

  return (
    <Modal
      visible={visible}
      title="框选区域编辑"
      footer={null}
      style={{ width: 920 }}
      maskClosable={!submitting}
      onCancel={onCancel}
    >
      <div className="region-editor" translate="no">
        <div className="region-hint">
          双击图片进入框选模式（再双击退回移动模式），拖动可移动放大后的图片；进入框选模式后在图片上拖动选择区域。
          框外内容会尽量保持不变。Enter 直接提交，Shift + Enter 换行。
        </div>
        <div className="region-toolbar" translate="no">
          <span className={`region-mode ${selectMode ? 'selecting' : ''}`}>
            {selectMode ? '框选模式' : '移动模式'}
          </span>
          <span className="region-zoom-label">缩放</span>
          <Button size="mini" disabled={zoom <= 1} onClick={() => setZoom(value => clampZoom(value - 0.25))}>
            −
          </Button>
          <span className="region-zoom-value">{Math.round(zoom * 100)}%</span>
          <Button size="mini" disabled={zoom >= 4} onClick={() => setZoom(value => clampZoom(value + 0.25))}>
            +
          </Button>
          <Button size="mini" disabled={zoom === 1} onClick={() => setZoom(1)}>
            适应窗口
          </Button>
        </div>
        <div className="region-viewport" ref={viewportRef}>
          <div
            className="region-canvas"
            style={{
              ...(zoomed && fit ? { width: fit.w * zoom, height: fit.h * zoom } : {}),
              cursor: selectMode ? 'crosshair' : panning ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleCanvasDown}
            onMouseMove={handleCanvasMove}
            onMouseUp={handleCanvasUp}
            onMouseLeave={handleCanvasUp}
            onDoubleClick={handleCanvasDoubleClick}
          >
            {imageUrl && (
              <img
                ref={imageRef}
                src={imageUrl}
                alt="待编辑图片"
                draggable={false}
                style={
                  zoomed
                    ? { width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none' }
                    : undefined
                }
                onLoad={handleImageLoad}
                onDragStart={event => event.preventDefault()}
              />
            )}
            {region && (
              <div
                className="region-selection"
                style={{
                  left: `${region.x * 100}%`,
                  top: `${region.y * 100}%`,
                  width: `${region.w * 100}%`,
                  height: `${region.h * 100}%`
                }}
              />
            )}
          </div>
        </div>
        <Input.TextArea
          className="region-instruction"
          value={instruction}
          onChange={setInstruction}
          onKeyDown={handleInstructionKeyDown}
          placeholder="例如：把这段文字改成“临床就绪”；或把这块背景换成深蓝色"
          autoSize={{ minRows: 2, maxRows: 5 }}
          maxLength={500}
          disabled={submitting}
        />
        <div className="region-meta">
          <span className="region-counter" translate="no">
            {instruction.length} / 500
          </span>
        </div>
        <div className="region-actions">
          <Button onClick={onCancel} disabled={submitting}>
            取消
          </Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!canSubmit}
            onClick={() => region && onSubmit(region, instruction.trim())}
          >
            按框选区域修改
          </Button>
        </div>
      </div>
    </Modal>
  );
}
